import { AccountCollectionFlowResponse } from 'src/queries/payments/payments.types'

/**
 * Presentation status derived from the server-authoritative archive timestamp
 * and payment method. The API does not persist this enum as a Vendor status.
 */
export enum VendorStatus {
  ActionRequired = 'ACTION_REQUIRED',
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
}

export interface VendorPaymentMethodResponse {
  id: string
  type: 'ACH'
  status: 'PAYABLE'
}

export interface VendorAccountCollectionFlowState {
  flowId: string
  status: 'PENDING' | 'AWAITING_ACKNOWLEDGEMENT'
  acknowledgementCopyVersion: string
}

/**
 * Realtime, display-safe projection of a vendor's payment method, fetched live
 * from Modern Treasury (`GET /payments/vendors/{vendorId}/payment-methods/
 * {paymentMethodId}`). Mirrors `VendorPaymentMethodDetailsResponse` in
 * extend-api (`VendorSchema.kt`). Raw account numbers never cross this
 * boundary; `accountNumberLast4` is MT's safe representation, and every bank
 * field is nullable — render a graceful fallback, never `undefined`.
 */
export interface VendorPaymentMethodDetailsResponse {
  id: string
  type: string
  status: string
  bankName?: string | null
  accountType?: string | null
  accountNumberLast4?: string | null
  routingNumber?: string | null
}

/** Plain app-domain Vendor cached by React Query. */
export interface Vendor {
  id: string
  name: string
  email: string
  externalId?: string
  contactName?: string
  phone?: string
  notes?: string
  status: VendorStatus
  paymentMethod: VendorPaymentMethodResponse | null
  accountCollectionFlow?: VendorAccountCollectionFlowState
  archivedAt?: string
  createdAt: string
  updatedAt: string
}

/**
 * Wire DTO returned by the API. NON_NULL serialization means every nullable
 * field may arrive as either null or be omitted entirely.
 */
export interface VendorResponse {
  id: string
  name: string
  email: string
  externalId?: string | null
  contactName?: string | null
  phoneNumber?: string | null
  description?: string | null
  paymentMethod?: VendorPaymentMethodResponse | null
  accountCollectionFlow?: VendorAccountCollectionFlowState | null
  archivedAt?: string | null
  createdAt: string
  updatedAt: string
}

/** The server archive timestamp is authoritative across wire and app models. */
export const isVendorArchived = (
  vendor: Pick<VendorResponse, 'archivedAt'>,
): boolean => vendor.archivedAt != null

export interface VendorPagination {
  page: number
  pageItemCount: number
  totalItems: number
  numberOfPages: number
}

export interface VendorsPageResponse {
  vendors: VendorResponse[]
  pagination: VendorPagination
}

export interface VendorsList {
  vendors: Vendor[]
  pagination: VendorPagination
}

export interface ListVendorsParams {
  page: number
  count: number
  search?: string
}

export interface CreateVendorRequest {
  name: string
  email: string
  externalId?: string
  contactName?: string
  phone?: string
  notes?: string
}

export interface CreateVendorMutationInput {
  request: CreateVendorRequest
  idempotencyKey: string
}

/**
 * Editable Step-1 details for the vendor edit flyout (EX-40771). Same domain
 * field shape as {@link CreateVendorRequest}, so the network layer reuses the
 * one wire mapper (`phone -> phoneNumber`, `notes -> description`). The PATCH is
 * a FULL REPLACE server-side: every call sends all six domain fields; an
 * omitted optional CLEARS the stored value (extend-api EX-40769, PR #10137).
 */
export type UpdateVendorDetailsRequest = CreateVendorRequest

/**
 * Input to `PATCH /payments/vendors/{vendorId}` with a `{ details: {...} }`
 * body. The acknowledgement is real: every details edit requires a fresh
 * re-acknowledgement (PO decision 2026-08-18), so `acknowledged` + the details
 * `acknowledgementCopyVersion` ride alongside `details`, mirroring the
 * account-collection confirm route. `acknowledged` is the literal `true` — an
 * unacknowledged details PATCH is unrepresentable by construction.
 */
export interface UpdateVendorDetailsMutationInput {
  vendorId: string
  request: UpdateVendorDetailsRequest
  acknowledged: true
  acknowledgementCopyVersion: string
}

/** Archive a vendor through the mutually exclusive `archived` PATCH operation. */
export interface ArchiveVendorMutationInput {
  vendorId: string
}

export type VendorAccountCollectionFlowResponse =
  AccountCollectionFlowResponse & {
    acknowledgementCopyVersion: string
  }

export interface ConfirmVendorAccountCollectionFlowInput {
  vendorId: string
  flowId: string
  acknowledged: true
  acknowledgementCopyVersion: string
}

export interface CompleteVendorAccountCollectionFlowInput {
  vendorId: string
  flowId: string
}

/**
 * Input to `POST /payments/vendors/{vendorId}/payment-methods/{paymentMethodId}
 * /retire` (EX-40770, extend-api #10132). No request body, no `Idempotency-Key`,
 * no `If-Match` — the call is genuinely idempotent server-side. Returns a full
 * `VendorResponse` whose `paymentMethod` key is absent (NON_NULL serialization),
 * so the vendor re-derives to `ActionRequired`.
 */
export interface RetireVendorPaymentMethodInput {
  vendorId: string
  paymentMethodId: string
}

export type VendorAccountCollectionFlowCompletionResponse = {
  status: 'PENDING' | 'AWAITING_ACKNOWLEDGEMENT' | 'CONFIRMED'
}

export type VendorAccountCollectionFlowConfirmationResponse =
  | {
      status: 'PENDING'
      paymentMethod?: never
    }
  | {
      status: 'CONFIRMED'
      paymentMethod: VendorPaymentMethodResponse
    }
